import { useEffect, useRef } from "react";
import type { DesignLabSizeTier } from "@design-lab/shared/lib/analytics";
import { trackPriceExposed } from "@design-lab/shared/lib/analytics";

// Module-level Set — deduplicates per page session, resets naturally on reload
const seenComponents = new Set<string>();

type UsePriceExposedTrackingOptions = {
  component: string;
  step: "PLP" | "DesignLab" | "Subcart" | "Checkout";
  priceShown: number;
  catalogNumber: string | undefined;
  quantity: number;
  businessLine: string;
  unitPrice?: number;
  totalPrice?: number;
  sizeTier?: DesignLabSizeTier;
};

export function usePriceExposedTracking(
  elementRef: React.RefObject<HTMLElement | null>,
  options: UsePriceExposedTrackingOptions,
  enabled = true
) {
  // Ref to avoid stale closures without options in dependency array
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled || seenComponents.has(options.component)) return undefined;
    if (options.priceShown <= 0 || options.quantity <= 0) return undefined;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !seenComponents.has(optionsRef.current.component)) {
            seenComponents.add(optionsRef.current.component);
            trackPriceExposed(optionsRef.current);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, enabled, options.component, options.priceShown, options.quantity]);
}
